<script>
import {
  pageTitleMixin,
  prefetchDataMixin,
} from '@ubic/vue-mixins';
import { permissions } from '@/constants';
import ErrorAccessDenied from '@/components/ErrorAccessDenied';
import FormError from '@/components/FormError';
import DeleteWithConfirm from '@/components/DeleteWithConfirm';
import ExternalIdsInput from './components/ExternalIdsInput';
import service from './service';

export default {
  pageTitle: 'Детали сотрудника',
  async prefetchData () {
    if (!this.hasReadPermission) {
      return;
    }

    this.isFetching = true;

    [this.worker, this.departmentOptions] = await Promise.all([
      service.getById(this.$route.params.id),
      this.hasDepartmentReadPermission
        ? service.getDepartmentOptions()
        : [],
    ]);

    this.name = this.worker.name || '';
    this.department = this.worker.department.id || '';
    this.sex = this.worker.sex || '';
    this.track = this.worker.track || false;
    this.externalIds = this.worker.externalIds || {};
    this.isFetching = false;
  },
  mixins: [
    pageTitleMixin,
    prefetchDataMixin,
  ],
  data () {
    return {
      isFetching: false,
      isSaving: false,
      departmentOptions: [],
      worker: {},
      department: '',
      name: '',
      track: false,
      sex: '',
      error: '',
      externalIds: {},
    };
  },
  computed: {
    hasReadPermission () {
      return this.$store
        .getters['auth/checkPermission'](permissions.WORKER_READ);
    },
    hasWritePermission () {
      return this.$store
        .getters['auth/checkPermission'](permissions.WORKER_WRITE);
    },
    hasDepartmentReadPermission () {
      return this.$store
        .getters['auth/checkPermission'](permissions.DEPARTMENT_READ);
    },
    hasAdminReadPermission () {
      return this.$store
        .getters['auth/checkPermission'](permissions.ADMIN_READ);
    },
  },
  methods: {
    handleNameInput (value) {
      this.name = value;
    },
    handleDepartmentInput (value) {
      this.department = value;
    },
    handleSexChange (value) {
      this.sex = value;
    },
    handleTrackChange (value) {
      this.track = value;
    },
    handleExternalIdsChange (value) {
      this.externalIds = value;
    },
    async handleSubmit (e) {
      e.preventDefault();

      if (this.isSaving || !this.hasWritePermission) {
        return;
      }

      this.error = '';
      this.isSaving = true;

      try {
        const { name, department, sex, track, externalIds } = this;
        const { id } = this.$route.params;

        await service.update(id, { name, department, sex, track, externalIds });

        this.emitDataUpdate();
        this.handleClose();
      } catch (error) {
        console.log(error);
        this.error = 'Ошибка при обновлении пользователя';
      }

      this.isSaving = false;
    },

    async handleDelete () {
      if (!this.hasWritePermission) {
        return;
      }

      if (this.isFetching) {
        return;
      }

      this.isFetching = true;

      try {
        await service.remove(this.$route.params.id);

        this.emitDataUpdate();
        this.handleClose();
      } catch (err) {
        this.error = 'Ошибка при удалении сотрудника';
        this.isFetching = false;
      }
    },

    emitDataUpdate () {
      this.$emit('dataupdate');
    },

    handleClose () {
      this.$emit('close');
      this.$router.push('/workers');
    },
  },
  render () {
    if (!this.hasReadPermission) {
      return (
        <u-modal onClose={this.handleClose}>
          <ErrorAccessDenied />
        </u-modal>
      );
    }

    if (this.isFetching) {
      return (
        <u-modal onClose={this.handleClose}>
          <u-spinner />
        </u-modal>
      );
    }

    const {
      $style,
      worker,
      name,
      department,
      sex,
      track,
      error,
      isSaving,
      hasWritePermission,
    } = this;

    return (
      <u-modal onClose={this.handleClose}>
        <u-title
          slot="header"
          level="2"
        >
          <span>
            Сотрудник
            &nbsp;
          </span>
          <span slot="sub">
            {worker.name}
          </span>
        </u-title>

        <form
          action="#"
          method="post"
          onSubmit={this.handleSubmit}
        >
          <div class="form-field">
            <div class="form-field__label">
              <label for="nameInput">
                Настоящее имя
              </label>
            </div>
            <u-input
              id="nameInput"
              required
              fullwidth
              name="name"
              static={!hasWritePermission}
              value={name}
              onInput={this.handleNameInput}
            />
          </div>
          <div class="form-field">
            <div class="form-field__label">
              Департамент
            </div>
            {(hasWritePermission && this.hasDepartmentReadPermission)
              ? (
                <u-select
                  id="departmentSelect"
                  name="department"
                  required
                  fullwidth
                  placeholder="не указан"
                  value={department}
                  options={this.departmentOptions}
                  onInput={this.handleDepartmentInput}
                />
              )
              : (
                <u-input
                  static
                  value={(worker.department || {}).name}
                  placeholder="Не указан"
                />
              )}
          </div>
          <div class="form-field">
            <div class="form-field__label">
              Пол
            </div>
            <div class={$style.radioList}>
              <div class={$style.radio}>
                <u-radio
                  id="sexRadio"
                  name="sex"
                  value="m"
                  disabled={!hasWritePermission}
                  checked={sex}
                  onChange={this.handleSexChange}
                >
                  мужской
                </u-radio>
              </div>
              <div class={$style.radio}>
                <u-radio
                  name="sex"
                  value="f"
                  disabled={!hasWritePermission}
                  checked={sex}
                  onChange={this.handleSexChange}
                >
                  женский
                </u-radio>
              </div>
            </div>
          </div>
          <div class="form-field">
            <u-checkbox
              name="track"
              value="m"
              checked={track}
              disabled={!hasWritePermission}
              onChange={this.handleTrackChange}
            >
              отслеживать
            </u-checkbox>
          </div>

          {Boolean(this.hasAdminReadPermission) && (
            <div>
              <u-title level="3">
                Внешние ключи
              </u-title>

              <ExternalIdsInput
                value={this.externalIds}
                onChange={this.handleExternalIdsChange}
              />
            </div>
          )}

          {Boolean(error) && (
            <FormError>
              {error}
            </FormError>
          )}

          {hasWritePermission && (
            <footer class="form-footer">
              <u-btn
                size="wide"
                type="submit"
              >
                {isSaving
                  ? <u-spinner inline />
                  : 'Сохранить'}
              </u-btn>

              <span class="m-l-auto">
                <DeleteWithConfirm onConfirm={this.handleDelete} />
              </span>
            </footer>
          )}
        </form>
      </u-modal>
    );
  },
};
</script>

<style lang="scss" module>
@import '@/theme/include.scss';

.radioList {
  display: flex;
}

.radio {
  & + & {
    margin-left: 3em;
  }
}

.error {
  color: $color-red-darker;
}
</style>
